/**
 * custom-shared-holiday-banner.js
 * Reveals <holiday-banner> (sections/custom-holiday-banner.liquid) when Sydney
 * is on an NSW public holiday, or — when the section allows — from the 1pm
 * cutoff on the last dispatch day before one.
 *
 * SHARED file — identical in ccc-horizon and phaser-horizon. Edit both.
 * Holiday dates: assets/custom-shared-public-holidays.js (review each October).
 */

import {
  getSydneyNowComponents,
  getSydneyToday,
  getPublicHolidayName,
  isNonDispatchDay,
  isWeekendDay,
  advanceToDispatchDay,
  formatLongDay,
} from '@theme/custom-shared-public-holidays';

const CUTOFF_HOUR = 13;
const DISMISS_KEY = 'ccc:holiday-banner-dismissed';

/**
 * @param {string} template
 * @param {Record<string, string>} values
 */
function fill(template, values) {
  return (template || '').replace(/\[(holiday|holiday_day|next_dispatch)\]/g, (_, key) => values[key] ?? '');
}

/**
 * Decide what, if anything, the banner should say right now.
 * @param {boolean} showEve
 * @returns {{kind: 'holiday'|'eve', holiday: string, holidayDate: Date, nextDispatch: Date}|null}
 */
function resolveState(showEve) {
  const today = getSydneyToday();
  const todayHoliday = getPublicHolidayName(today);

  if (todayHoliday) {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return { kind: 'holiday', holiday: todayHoliday, holidayDate: today, nextDispatch: advanceToDispatchDay(tomorrow) };
  }

  if (!showEve) return null;

  // Eve: today is a dispatch day past the cutoff (or a weekend), and the next
  // weekday before the next dispatch day is a public holiday.
  const now = getSydneyNowComponents();
  const todayDispatches = !isNonDispatchDay(today);
  if (todayDispatches && now.hour < CUTOFF_HOUR) return null;

  const probe = new Date(today);
  probe.setDate(probe.getDate() + 1);
  const next = advanceToDispatchDay(probe);

  // Walk the gap looking for a weekday holiday (a weekend alone is ordinary).
  const d = new Date(probe);
  while (d.getTime() < next.getTime()) {
    const name = getPublicHolidayName(d);
    if (name && !isWeekendDay(d)) {
      return { kind: 'eve', holiday: name, holidayDate: new Date(d), nextDispatch: next };
    }
    d.setDate(d.getDate() + 1);
  }
  return null;
}

class HolidayBanner extends HTMLElement {
  connectedCallback() {
    this._headingEl = this.querySelector('[data-chb-heading]');
    this._textEl = this.querySelector('[data-chb-text]');
    this._closeEl = this.querySelector('[data-chb-close]');
    this._onClose = () => this._dismiss();
    if (this._closeEl) this._closeEl.addEventListener('click', this._onClose);
    this._render();
    // Re-evaluate each minute so the 1pm cutoff and midnight flip without a reload.
    this._timer = window.setInterval(() => this._render(), 60000);
  }

  disconnectedCallback() {
    if (this._timer) window.clearInterval(this._timer);
    if (this._closeEl) this._closeEl.removeEventListener('click', this._onClose);
  }

  /** One dismissal covers one notice (holiday-or-eve + date); the next one shows again. */
  _dismissToken(state) {
    return `${state.kind}:${state.holidayDate.getFullYear()}-${state.holidayDate.getMonth() + 1}-${state.holidayDate.getDate()}`;
  }

  _isDismissed(state) {
    try {
      return localStorage.getItem(DISMISS_KEY) === this._dismissToken(state);
    } catch {
      return false;
    }
  }

  _dismiss() {
    if (this._state) {
      try {
        localStorage.setItem(DISMISS_KEY, this._dismissToken(this._state));
      } catch {
        /* private mode — banner just hides for this page view */
      }
    }
    this.hidden = true;
  }

  _render() {
    const showEve = this.dataset.showEve === 'true';
    const state = resolveState(showEve);

    this._state = state;
    if (!state || this._isDismissed(state)) {
      this.hidden = true;
      return;
    }

    const values = {
      holiday: state.holiday,
      holiday_day: formatLongDay(state.holidayDate),
      next_dispatch: formatLongDay(state.nextDispatch),
    };

    const heading = state.kind === 'holiday' ? this.dataset.heading : this.dataset.eveHeading;
    const text = state.kind === 'holiday' ? this.dataset.text : this.dataset.eveText;

    if (this._headingEl) this._headingEl.textContent = fill(heading, values);
    if (this._textEl) this._textEl.textContent = fill(text, values);
    this.dataset.state = state.kind;
    this.hidden = false;
  }
}

if (!customElements.get('holiday-banner')) {
  customElements.define('holiday-banner', HolidayBanner);
}
